import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Box, Tooltip } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { SCUIContent } from '../../atomic/organisms';
import { SCUICustomDataGrid, SCUIGeneralDrawer, SCUIBreadCrumb } from '../../atomic/molecules';
import { SyncTableStateQuery } from '../../services/query';
import { SyncTableStateCommand } from '../../services/command';
import { getColumns, initialState } from './SyncTableState.helper';
import { SyncTableStateForm } from './components/SyncTableStateForm';
import Swal from 'sweetalert2';
import { SCUILoading } from '../../atomic/atoms';

export const SyncTableState: FC = () => {
    const { t } = useTranslation();
    const [state, setState] = useState(initialState);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setState((prev) => ({ ...prev, loading: true }));
        try {
            const response = await SyncTableStateQuery.GetSyncTableStates();
            const items = Array.isArray(response) ? response : response?.data || [];
            setState((prev) => ({
                ...prev,
                tableStateData: items,
                loading: false,
            }));
        } catch (error) {
            setState((prev) => ({ ...prev, loading: false }));
            console.error('Error loading sync table states:', error);
        }
    };

    const handleEdit = (row: any) => {
        setState((prev) => ({
            ...prev,
            drawerOpen: true,
            selectedRow: row,
        }));
    };

    const handleSave = async (formData: any) => {
        try {
            await SyncTableStateCommand.UpdateSyncTableState(formData.id, {
                batchSize: formData.batchSize,
                isActive: formData.isActive,
            });
            Swal.fire({
                icon: 'success',
                title: t('general.success'),
                text: t('studioSyncTableState.updateSuccess'),
                timer: 2000,
            });
            setState((prev) => ({ ...prev, drawerOpen: false, selectedRow: null }));
            loadData();
        } catch {
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('studioSyncTableState.updateFailed'),
            });
        }
    };

    const handleCloseDrawer = () => {
        setState((prev) => ({ ...prev, drawerOpen: false, selectedRow: null }));
    };

    const actionColumn = {
        field: 'actions',
        headerName: t('general.actions'),
        flex: 0.6,
        minWidth: 100,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => (
            <Tooltip title={t('general.edit')}>
                <Button size="small" onClick={() => handleEdit(params.row)}>
                    <EditIcon fontSize="small" />
                </Button>
            </Tooltip>
        ),
    };

    if (state.loading) {
        return <SCUILoading />;
    }

    return (
        <SCUIContent>
            <SCUIBreadCrumb
                items={[
                    { label: t('general.home'), path: '/' },
                    { label: t('studioSyncTableState.title') },
                ]}
            />
            <Box sx={{ mb: 2 }}>
                <h2>{t('studioSyncTableState.title')}</h2>
                <p>{t('studioSyncTableState.description')}</p>
            </Box>
            <SCUICustomDataGrid
                rows={state.tableStateData}
                columns={[...getColumns(t), actionColumn]}
                getRowId={(row: any) => row.id}
                autoHeight
            />
            <SCUIGeneralDrawer open={state.drawerOpen} onClose={handleCloseDrawer}>
                <SyncTableStateForm data={state.selectedRow} onSave={handleSave} onClose={handleCloseDrawer} />
            </SCUIGeneralDrawer>
        </SCUIContent>
    );
};
